import { userIdFromAgentIdentity } from "./voOfficeLife";

export const CHAT_MENTION_ALL_ID = "all";
export const CHAT_MENTION_ALL_LABEL = "todos";

const ALL_IDS = new Set(["all", "todos"]);
const ALL_LABELS = new Set(["todos", "all", "everyone", "tutti"]);
const MENTION_TOKEN_RE = /@\[([^\]]+)\]\(([^)]+)\)/g;

function clipName(raw) {
  return String(raw || "")
    .replace(/[[\]]/g, "")
    .trim();
}

function clipIdentity(raw) {
  return String(raw || "")
    .replace(/[)\s]/g, "")
    .trim();
}

export function encodeMentionToken(name, identity) {
  const safeName = clipName(name) || "Colega";
  const safeId = clipIdentity(identity);
  if (!safeId) return `@${safeName}`;
  return `@[${safeName}](${safeId})`;
}

export function personKey(person) {
  const uid = Number(person?.userId);
  if (Number.isFinite(uid) && uid > 0) return `u:${uid}`;
  const identity = String(person?.identity || "").trim();
  if (identity) return `i:${identity}`;
  const name = String(person?.name || "")
    .trim()
    .toLowerCase();
  return name ? `n:${name}` : "";
}

export function normalizeChatMentionPerson(raw) {
  if (!raw || typeof raw !== "object") return null;
  if (raw.type === "all" || ALL_IDS.has(String(raw.identity || raw.id || "").toLowerCase())) {
    return {
      type: "all",
      id: CHAT_MENTION_ALL_ID,
      identity: CHAT_MENTION_ALL_ID,
      name: clipName(raw.name) || CHAT_MENTION_ALL_LABEL,
      userId: null,
    };
  }
  const identity = clipIdentity(raw.identity || raw.twilioIdentity);
  const name = String(raw.name || raw.displayName || raw.userName || "").trim();
  const userIdRaw = Number(raw.userId || raw.id);
  const userId =
    Number.isFinite(userIdRaw) && userIdRaw > 0
      ? userIdRaw
      : userIdFromAgentIdentity(identity);
  if (!name && !identity) return null;
  return {
    type: "user",
    identity,
    name: name || "Participante",
    userId: userId || null,
  };
}

export function mergeChatMentionPeople(lists = [], { excludeIdentity, excludeUserId } = {}) {
  const map = new Map();
  const selfIdentity = String(excludeIdentity || "").trim();
  const selfUserId = Number(excludeUserId);
  for (const list of lists) {
    for (const raw of Array.isArray(list) ? list : []) {
      const person = normalizeChatMentionPerson(raw);
      if (!person || person.type === "all") continue;
      if (selfIdentity && person.identity && person.identity === selfIdentity) continue;
      if (Number.isFinite(selfUserId) && selfUserId > 0 && Number(person.userId) === selfUserId) {
        continue;
      }
      const key = personKey(person);
      if (!key) continue;
      const prev = map.get(key);
      if (!prev) {
        map.set(key, person);
        continue;
      }
      map.set(key, {
        ...prev,
        ...person,
        name: person.name && person.name !== "Participante" ? person.name : prev.name,
        identity: person.identity || prev.identity,
        userId: person.userId || prev.userId,
      });
    }
  }
  return [...map.values()].sort((a, b) =>
    String(a.name).localeCompare(String(b.name), undefined, { sensitivity: "base" })
  );
}

/**
 * Localiza @ ativo na posição do cursor (menção no meio do texto).
 * @returns {{ atIndex: number, query: string } | null}
 */
export function findActiveMentionAt(text, cursorIndex) {
  const plain = String(text || "");
  const len = plain.length;
  const cursor = Math.max(
    0,
    Math.min(Number.isFinite(Number(cursorIndex)) ? Number(cursorIndex) : len, len)
  );
  const atIndex = plain.lastIndexOf("@", cursor - 1);
  if (atIndex < 0) return null;
  if (atIndex > 0 && !/\s/.test(plain[atIndex - 1])) return null;
  const query = plain.slice(atIndex + 1, cursor);
  if (/\s/.test(query) || query.includes("[") || query.includes("]")) return null;
  return { atIndex, query };
}

export function insertMentionAt(text, cursorIndex, person) {
  const name =
    String(person?.name || "").trim() ||
    (person?.type === "all" ? CHAT_MENTION_ALL_LABEL : "Colega");
  const insertion = `@${name} `;
  const active = findActiveMentionAt(text, cursorIndex);
  if (!active) {
    const next = `${String(text || "")}${insertion}`;
    return { text: next, cursor: next.length };
  }
  const src = String(text || "");
  const before = src.slice(0, active.atIndex);
  const after = src.slice(
    Number.isFinite(Number(cursorIndex)) ? Number(cursorIndex) : src.length
  );
  const next = `${before}${insertion}${after}`;
  return { text: next, cursor: before.length + insertion.length };
}

export function buildChatMentionOptions(people = [], query = "", { allLabel = CHAT_MENTION_ALL_LABEL } = {}) {
  const q = String(query || "")
    .toLowerCase()
    .trim();
  const options = [];
  if (!q || allLabel.toLowerCase().startsWith(q) || "all".startsWith(q) || q === "tod") {
    options.push({
      type: "all",
      id: CHAT_MENTION_ALL_ID,
      identity: CHAT_MENTION_ALL_ID,
      name: allLabel,
    });
  }
  const filtered = (people || []).filter((p) => {
    if (!q) return true;
    return (
      String(p.name || "")
        .toLowerCase()
        .includes(q) ||
      String(p.identity || "")
        .toLowerCase()
        .includes(q)
    );
  });
  return [
    ...options,
    ...filtered.map((p) => ({
      type: "user",
      id: personKey(p) || p.identity || p.name,
      identity: p.identity,
      name: p.name,
      userId: p.userId || null,
    })),
  ];
}

function mentionDedupeKey(mention) {
  if (!mention) return "";
  if (mention.type === "all" || ALL_IDS.has(String(mention.identity || "").toLowerCase())) {
    return "all";
  }
  return personKey(mention);
}

export function dedupeChatMentions(list = []) {
  const map = new Map();
  for (const raw of list) {
    const person = normalizeChatMentionPerson(raw);
    if (!person) continue;
    const key = mentionDedupeKey(person);
    if (!key) continue;
    if (!map.has(key)) map.set(key, person);
  }
  return [...map.values()];
}

function escapeRegExp(value) {
  return String(value).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function isInsideEncodedToken(src, offset) {
  const before = src.slice(Math.max(0, offset - 2), offset);
  return before.endsWith("[") || /@\[[^\]]*$/.test(src.slice(0, offset));
}

export function parseChatMentions(text, people = []) {
  const body = String(text || "");
  const mentions = [];
  let mentionAll = false;

  const tokenRe = new RegExp(MENTION_TOKEN_RE.source, "g");
  let token;
  while ((token = tokenRe.exec(body))) {
    const name = clipName(token[1]);
    const identity = clipIdentity(token[2]);
    if (ALL_IDS.has(identity.toLowerCase()) || ALL_IDS.has(name.toLowerCase())) {
      mentionAll = true;
      continue;
    }
    mentions.push({
      type: "user",
      name: name || "Participante",
      identity,
      userId: userIdFromAgentIdentity(identity),
    });
  }

  const allLabelRe = [...ALL_LABELS].map(escapeRegExp).join("|");
  if (new RegExp(`(^|[\\s])@(${allLabelRe})\\b`, "i").test(body)) mentionAll = true;

  const remaining = body.replace(new RegExp(MENTION_TOKEN_RE.source, "g"), " ");
  const catalog = dedupeChatMentions(people).sort(
    (a, b) => String(b.name || "").length - String(a.name || "").length
  );
  for (const person of catalog) {
    const name = String(person.name || "").trim();
    if (!name) continue;
    const re = new RegExp(`@${escapeRegExp(name)}(?=\\s|$|[.,!?;:])`, "i");
    if (!re.test(remaining)) continue;
    mentions.push(person);
  }

  return { mentions: dedupeChatMentions(mentions), mentionAll };
}

export function encodeChatMessageBody(text, { people = [], picked = [] } = {}) {
  const parsed = parseChatMentions(text, people);
  const mentionAll =
    parsed.mentionAll ||
    (picked || []).some(
      (p) => p?.type === "all" || ALL_IDS.has(String(p?.identity || p?.id || "").toLowerCase())
    );
  const mentions = dedupeChatMentions([
    ...parsed.mentions,
    ...(picked || []).filter(
      (p) => p && p.type !== "all" && !ALL_IDS.has(String(p.identity || p.id || "").toLowerCase())
    ),
  ]);

  let body = String(text || "");
  const toEncode = [...mentions].sort(
    (a, b) => String(b.name || "").length - String(a.name || "").length
  );
  for (const mention of toEncode) {
    const name = String(mention.name || "").trim();
    const identity = clipIdentity(mention.identity);
    if (!name || !identity) continue;
    const re = new RegExp(`@${escapeRegExp(name)}(?=\\s|$|[.,!?;:])`, "g");
    body = body.replace(re, (match, offset, src) => {
      if (isInsideEncodedToken(src, offset)) return match;
      return encodeMentionToken(name, identity);
    });
  }
  if (mentionAll) {
    const labels = new Set(ALL_LABELS);
    for (const pickedPerson of picked || []) {
      if (pickedPerson?.type === "all" && pickedPerson.name) {
        labels.add(String(pickedPerson.name).toLowerCase());
      }
    }
    const labelRe = [...labels].map(escapeRegExp).join("|");
    body = body.replace(new RegExp(`(^|[\\s])@(${labelRe})\\b`, "gi"), (full) => {
      const at = full.lastIndexOf("@");
      return `${full.slice(0, at)}${encodeMentionToken(CHAT_MENTION_ALL_LABEL, CHAT_MENTION_ALL_ID)}`;
    });
  }
  return { body, mentions, mentionAll };
}

export function chatMentionSegments(text) {
  const body = String(text || "");
  const segments = [];
  const re = /@\[([^\]]+)\]\(([^)]+)\)|@(todos|all)\b/gi;
  let last = 0;
  let match;
  while ((match = re.exec(body))) {
    if (match.index > last) {
      segments.push({ type: "text", text: body.slice(last, match.index) });
    }
    if (match[1] != null) {
      const identity = clipIdentity(match[2]);
      const name = clipName(match[1]) || "Colega";
      const isAll = ALL_IDS.has(identity.toLowerCase()) || ALL_IDS.has(name.toLowerCase());
      segments.push({
        type: "mention",
        text: `@${name}`,
        identity,
        mentionAll: isAll,
        userId: isAll ? null : userIdFromAgentIdentity(identity),
      });
    } else {
      segments.push({
        type: "mention",
        text: `@${match[3]}`,
        identity: CHAT_MENTION_ALL_ID,
        mentionAll: true,
        userId: null,
      });
    }
    last = match.index + match[0].length;
  }
  if (last < body.length) segments.push({ type: "text", text: body.slice(last) });
  return segments.length ? segments : [{ type: "text", text: body }];
}

export function isChatMentionForMe(payload, { identity, userId } = {}) {
  if (!payload) return false;
  if (payload.mentionAll) return true;
  const mineIdentity = String(identity || "").trim();
  const mineUserId = Number(userId);
  const mentions = Array.isArray(payload.mentions) ? payload.mentions : [];
  for (const mention of mentions) {
    if (ALL_IDS.has(String(mention?.identity || "").toLowerCase())) return true;
    if (mineIdentity && String(mention?.identity || "").trim() === mineIdentity) return true;
    if (
      Number.isFinite(mineUserId) &&
      mineUserId > 0 &&
      Number(mention?.userId) === mineUserId
    ) {
      return true;
    }
  }
  const parsed = parseChatMentions(payload.body || "", []);
  if (parsed.mentionAll) return true;
  for (const mention of parsed.mentions) {
    if (mineIdentity && String(mention.identity || "").trim() === mineIdentity) return true;
    if (
      Number.isFinite(mineUserId) &&
      mineUserId > 0 &&
      Number(mention.userId) === mineUserId
    ) {
      return true;
    }
  }
  return false;
}
